import { CopyX } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button, Confirm } from "./ui";

type Pair = { a: any; b: any };

export function DupBanner({ rows, onDone }: { rows: any[]; onDone: () => void }) {
  const [confirm, setConfirm] = useState<{ keep: any; remove: any } | null>(null);
  const [busy, setBusy] = useState(false);
  void busy;
  const pairs: Pair[] = rows.filter((s) => s.dup_flag && s.dup_of && s.dup_partner).map((s) => ({ a: s.dup_partner, b: s }));
  if (!pairs.length) return null;

  const resolve = async () => {
    if (!confirm) return;
    setBusy(true);
    try { await api.post("/students/dup-resolve", { keep_id: confirm.keep.id, remove_id: confirm.remove.id }); toast.success(`${confirm.remove.internal_id} dihapus · ${confirm.keep.internal_id} dipertahankan`); setConfirm(null); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ignore = async (p: Pair) => {
    try { await api.post("/students/dup-ignore", { keep_id: p.a.id, remove_id: p.b.id }); toast.success("Ditandai bukan duplikat"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const Side = ({ s, other }: { s: any; other: any }) => (
    <div className="rounded-lg bg-white/80 dark:bg-white/5 border border-pink-200 dark:border-pink-800 p-2 flex-1 min-w-[220px]">
      <p className="font-semibold text-sm">{s.name}</p>
      <p className="text-xs text-muted-foreground num">{s.internal_id} · NIK {String(s.nik).startsWith("PENDING") ? "belum ada" : s.nik}{s.dup_of ? " · baru diimport" : " · data lama"}</p>
      {(s.loan_count ?? (s.active_loan ? 1 : 0)) > 0 && <p className="text-[11px] font-semibold text-red-700 dark:text-red-300 mt-0.5">Punya {s.loan_count ?? 1} pinjaman — ikut terhapus jika tidak dipertahankan</p>}
      <Button size="sm" className="mt-2 w-full bg-pink-600 hover:bg-pink-700" data-testid={`dup-keep-${s.internal_id}`} onClick={() => setConfirm({ keep: s, remove: other })}>Pertahankan ini</Button>
    </div>
  );
  return (
    <div className="mb-3 rounded-xl border-2 border-pink-400 bg-pink-50 dark:bg-pink-950/30 dark:border-pink-800 p-4" data-testid="dup-banner">
      <div className="flex items-center gap-2 text-pink-800 dark:text-pink-200 font-bold"><CopyX className="h-5 w-5" /> {pairs.length} data duplikat terdeteksi dari import — pilih yang dipertahankan</div>
      <p className="text-xs text-pink-700/80 dark:text-pink-300/80 mt-0.5">Baris pink di tabel adalah data yang sama (nama/NIK identik). Yang tidak dipertahankan akan dihapus beserta pinjaman import-nya (jika belum ada pembayaran).</p>
      <div className="mt-3 space-y-2">
        {pairs.map((p) => (
          <div key={p.b.id} className="flex flex-col sm:flex-row gap-2 sm:items-stretch" data-testid={`dup-pair-${p.b.internal_id}`}>
            <Side s={p.a} other={p.b} />
            <Side s={p.b} other={p.a} />
            <button type="button" className="text-xs font-semibold text-muted-foreground hover:text-foreground self-center px-2 whitespace-nowrap" data-testid={`dup-ignore-${p.b.internal_id}`} onClick={() => ignore(p)}>Bukan duplikat</button>
          </div>
        ))}
      </div>
      <Confirm open={!!confirm} onClose={() => setConfirm(null)} onConfirm={resolve} danger title="Hapus data duplikat?" text={confirm ? `${confirm.remove.name} (${confirm.remove.internal_id}) akan dihapus${(confirm.remove.loan_count ?? (confirm.remove.active_loan ? 1 : 0)) > 0 ? ` BESERTA ${confirm.remove.loan_count ?? 1} PINJAMAN & jadwal cicilannya` : ""}. ${confirm.keep.internal_id} dipertahankan. Tindakan ini tidak bisa dibatalkan.` : ""} testId="dup-confirm" />
    </div>
  );
}
